import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AssetSummary } from "../api/assets";
import { assetTypesApi, isRangeValue, type AttributeDefView } from "../api/catalog";
import type { ApiError } from "../api/client";
import { ConditionChip, UsageChip } from "../components/ConditionChip";
import { formatDate } from "../lib/dates";
import { formatRange } from "./AttributeValuesView";
import { shortPathLabel } from "../lib/paths";
import { foldText } from "../lib/foldText";

/** Upper bound of rows fetched in one go; a plant has hundreds of assets per type, not thousands. */
const PAGE_SIZE = 500;

type SortKey = "tag" | "serialNo" | "condition" | "usage" | "position" | "location" | `attr:${string}`;

type Props = { typeId: string; /** "ŠIFRA Naziv" of the type, shown in the card title. */ typeLabel?: string };

/**
 * "Svi atributi" view of the part search (Strahinja, 2026-09-22): every asset of the
 * chosen type in one wide table, one column per attribute definition of that type
 * with the asset's actual value. Client-side search and column sort — the whole
 * type fits in one page. Old rows stay dimmed while a new type loads (hard rule 11).
 */
export function AssetAttributesTable({ typeId, typeLabel }: Props) {
  const { t } = useTranslation();
  const [defs, setDefs] = useState<AttributeDefView[] | null>(null);
  const [rows, setRows] = useState<AssetSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "tag", dir: 1 });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      assetTypesApi.attributeDefs(typeId),
      assetsApi.list({ typeId, size: PAGE_SIZE, sort: "tag" }),
    ])
      .then(([d, page]) => {
        if (cancelled) return;
        setDefs([...d].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)));
        setRows(page.content);
        setTotal(page.totalElements);
      })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [typeId]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const needle = foldText(q.trim());
    const filtered = needle
      ? rows.filter((a) => foldText(rowText(a, defs ?? [])).includes(needle))
      : rows;
    const cmp = comparator(sort.key, defs ?? []);
    return [...filtered].sort((a, b) => cmp(a, b) * sort.dir);
  }, [rows, defs, q, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  const th = (key: SortKey, label: string, extra?: string) => (
    <th key={key} aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      <button type="button" className="th-sort" onClick={() => toggleSort(key)}>
        {label}{extra && <span className="muted"> ({extra})</span>}
        {sort.key === key && <span aria-hidden="true">{sort.dir === 1 ? " ▲" : " ▼"}</span>}
      </button>
    </th>
  );

  if (error) return <div className="card"><div style={errorBox} role="alert">{error}</div></div>;
  if (!rows || !defs) return <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>;

  return (
    <section className="card" style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={loading}>
      <div className="card-h" style={{ gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3>{t("partSearch.attrs.title", "Svi atributi")}{typeLabel && <span className="muted" style={{ fontWeight: 400 }}> — {typeLabel}</span>}</h3>
          <div className="sub">
            {t("partSearch.attrs.subtitle", "{{count}} asseta ovog tipa · {{attrs}} atributa", { count: total, attrs: defs.length })}
            {total > rows.length && (
              <> · {t("partSearch.attrs.truncated", "prikazano prvih {{shown}}", { shown: rows.length })}</>
            )}
          </div>
        </div>
        <input
          className="input"
          style={{ maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("partSearch.attrs.search", "Pretraga po oznaci, serijskom, vrednosti…")}
          aria-label={t("partSearch.attrs.search", "Pretraga po oznaci, serijskom, vrednosti…")}
        />
      </div>
      {rows.length === 0 ? (
        <div className="empty">{t("partSearch.noneAtAll", "Nema nijednog asseta ovog tipa u registru.")}</div>
      ) : (
        <div className="tbl-scroll">
          <table className="tbl compact attr-table">
            <thead>
              <tr>
                {th("tag", t("assets.col.tag", "Oznaka"))}
                {th("serialNo", t("assets.field.serialNo", "Serijski broj"))}
                {th("condition", t("assets.col.condition", "Stanje"))}
                {th("usage", t("assets.col.usage", "Upotreba"))}
                {th("position", t("assets.col.position", "Logička pozicija"))}
                {th("location", t("assets.col.location", "Fizička lokacija"))}
                {defs.map((d) => th(`attr:${d.key}`, d.label, d.unit ?? undefined))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={6 + defs.length} className="muted">{t("partSearch.attrs.noMatch", "Nijedan asset ne odgovara pretrazi.")}</td></tr>
              ) : visible.map((a) => {
                const pos = a.positionId ? shortPathLabel(a.positionPath, a.positionName) : null;
                return (
                  <tr key={a.id}>
                    <td className="mono"><Link to={`/asseti/${a.id}`}><b>{a.tag}</b></Link></td>
                    <td className="mono">{a.serialNo ?? <span className="muted">—</span>}</td>
                    <td><ConditionChip condition={a.condition} /></td>
                    <td><UsageChip usage={a.usage} /></td>
                    <td>{pos && a.positionId ? <Link to={`/struktura/${a.positionId}`} title={pos.full}>{pos.short}</Link> : <span className="muted">—</span>}</td>
                    <td>{a.effectiveLocation ? a.effectiveLocation.name : <span className="muted">—</span>}</td>
                    {defs.map((d) => (
                      <td key={d.key} className={d.dataType === "NUMBER" ? "num" : undefined}>
                        {formatValue(t, d, a.actualValues?.[d.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type Translate = (key: string, fallback: string) => string;

/** Value cell: booleans as Da/Ne, dates in the app format, numbers as typed, blanks as an em dash. */
export function formatValue(t: Translate, def: Pick<AttributeDefView, "dataType" | "unit">, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  switch (def.dataType) {
    case "BOOLEAN":
      return v === true || v === "true" ? t("common.yes", "Da") : t("common.no", "Ne");
    case "DATE":
      return typeof v === "string" ? formatDate(v) : String(v);
    case "RANGE":
      return isRangeValue(v) ? formatRange(v, def.unit) : String(v);
    default:
      return String(v);
  }
}

function rowText(a: AssetSummary, defs: AttributeDefView[]): string {
  const parts = [a.tag, a.serialNo ?? "", a.positionName ?? "", a.effectiveLocation?.name ?? ""];
  for (const d of defs) {
    const v = a.actualValues?.[d.key];
    if (v !== null && v !== undefined) parts.push(String(v));
  }
  return parts.join(" ");
}

function comparator(key: SortKey, defs: AttributeDefView[]): (a: AssetSummary, b: AssetSummary) => number {
  if (key.startsWith("attr:")) {
    const k = key.slice(5);
    const numeric = defs.find((d) => d.key === k)?.dataType === "NUMBER";
    return (a, b) => {
      const va = a.actualValues?.[k];
      const vb = b.actualValues?.[k];
      // Blanks sort last regardless of direction being flipped by the caller.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (numeric) return Number(va) - Number(vb);
      return String(va).localeCompare(String(vb), "sr");
    };
  }
  const pick = (a: AssetSummary): string => {
    switch (key) {
      case "tag": return a.tag;
      case "serialNo": return a.serialNo ?? "";
      case "condition": return a.condition;
      case "usage": return a.usage;
      case "position": return a.positionName ?? "";
      case "location": return a.effectiveLocation?.name ?? "";
      default: return "";
    }
  };
  return (a, b) => pick(a).localeCompare(pick(b), "sr");
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
